// The Lender Hub as a vendor (plan v2 §3.4), with no change to the Hub's code: its lenders come from the Hub's
// directory, each lender's routes from `GET /lenders/{slug}/_hub/endpoints`, and a call is
// `POST /lenders/{slug}/{route}` with our consumer key (x-hub-key). The Hub has no "kind" on a route, so we pick
// one per route, once: dedupe / prefill (gives a link) / status / other. The workflow's lender API node offers
// lender → kind → route. We keep only the outcome and the link of an answer, never the lender's raw reply.
import { z } from 'zod'
import { CountAnswer, Id, IsoDateTime } from './common'
import { ApiCounters, ApiHealth } from './vendor-apis'
import { NODE_PORTS, NodeType } from './workflows'

export const RouteKind = z.enum(['dedupe', 'prefill', 'status', 'other'])
export type RouteKind = z.infer<typeof RouteKind>

/** The outcomes a lender's code can answer (the Hub's contracts/hub/sdk-outcome.v1.json). */
export const HubOutcome = z.enum([
  'ok',
  'offer',
  'approved',
  'redirect',
  'pending',
  'rejected',
  'duplicate',
  'error',
])
export type HubOutcome = z.infer<typeof HubOutcome>

/**
 * R1: every lender call needs the full person (mobile, PAN, name, DOB, monthly income, employment, PIN code) and a
 * consent receipt. R3: a prefill route whose code never answers `redirect` + a link.
 */
export const RoadBlock = z.enum(['R1', 'R3'])
export type RoadBlock = z.infer<typeof RoadBlock>

export const HubRoute = ApiCounters.extend({
  id: Id,
  lender_slug: z.string(),
  lender_name: z.string(),
  method: z.string(),
  path: z
    .string()
    .describe('the route inside the lender, e.g. /dedupe; called as POST /lenders/{slug}{path}'),
  outcomes: z.array(HubOutcome).describe("from the lender's /_hub/endpoints"),
  title: z.string().nullable().describe("the API's title in the Hub, e.g. Personal loan · create lead (PD64)"),
  takes: z
    .array(z.string())
    .describe('PD64: the applicant fields the API reads (the Hub reads them from its code), e.g. mobile, pan'),
  gives: z
    .array(z.string())
    .describe(
      "PD64: the answer fields the API returns: lender_ref (the lender's own lead id), link (where the person continues), offers, data.<key>; what a message can carry"
    ),
  kind: RouteKind.nullable().describe(
    'null until someone picks it; only routes with a kind reach the workflow'
  ),
  gives_link: z.boolean().describe('its outcomes include redirect (a link for the person)'),
  status: ApiHealth,
  used_by: z.array(z.string()).describe('the workflow versions whose lender API node uses it'),
  road_blocks: z.array(RoadBlock),
})
export type HubRoute = z.infer<typeof HubRoute>

export const HubLender = z.object({
  slug: z.string(),
  name: z.string(),
  routes: z.number().int().min(0),
  kinds_picked: z.number().int().min(0),
  calls_today: z.number().int().min(0),
  errors_today: z.number().int().min(0),
  last_call_at: IsoDateTime.nullable(),
  synced_at: IsoDateTime.describe('when the directory and its routes were last read from the Hub'),
  connection: z
    .enum(['link', 'api', 'manual'])
    .nullable()
    .describe(
      'PD64, from the Hub: link = the person goes to the lender by its UTM link; api = we send the application through its API; manual = our team enters the lead; null = not read yet'
    ),
  link_url: z
    .string()
    .nullable()
    .describe(
      "W1 (R2): the lender's own UTM link from the Hub (the one Apply now uses), exactly as the lender gave it"
    ),
})
export type HubLender = z.infer<typeof HubLender>

export const HubLenderDetail = z.object({ lender: HubLender, routes: z.array(HubRoute) })
export type HubLenderDetail = z.infer<typeof HubLenderDetail>

export const SetRouteKindInput = z.object({ kind: RouteKind })
export type SetRouteKindInput = z.infer<typeof SetRouteKindInput>

const LENDER_NODE = NodeType.enum['campaign.http_preset']
// the schema lists the lender API node's ports in this order: pass, fail, next, link, no_link
const [PASS, FAIL, NEXT, LINK, NO_LINK] = NODE_PORTS[LENDER_NODE] as [string, string, string, string, string]

/**
 * The ports a lender API node leaves by: a dedupe route answers pass / fail, a prefill route link / no_link (the
 * lender's answer had a link for the person, or not: error, rejected, or a route that never gives one, R3), every
 * other kind goes on by next.
 */
export function lenderApiPorts(kind: RouteKind | null | undefined): readonly string[] {
  if (kind === RouteKind.enum.dedupe) return [PASS, FAIL]
  return kind === RouteKind.enum.prefill ? [LINK, NO_LINK] : [NEXT]
}

export interface LenderHubApi {
  listHubLenders(): Promise<HubLender[]>
  getHubLender(slug: string): Promise<HubLenderDetail>
  /** every route of every lender (the workflow's lender API node offers those with a kind) */
  listHubRoutes(): Promise<HubRoute[]>
  setRouteKind(routeId: string, input: SetRouteKindInput): Promise<HubRoute>
  /** read the directory and every lender's routes again; answers how many lenders were read */
  syncHub(): Promise<CountAnswer>
}
